/**
 * Layar **login PIN** — pintu masuk kasir & pelayan.
 *
 * ⛔ Tanpa ini, seluruh izin POS yang sudah ditegakkan server tak punya sumber: pelaku akan
 * kembali datang dari body permintaan, dan pemeriksaan izin atas pelaku yang bisa dikarang adalah
 * teater.
 *
 * ### ⭐ Keypad angka DI LAYAR, bukan keyboard sistem
 * Di tablet, keyboard OS menutupi separuh layar dan **menggeser tata letak** tepat saat jari sudah
 * mengarah ke tombolnya. PIN itu angka; keypad sendiri berarti letak tombol tak pernah bergerak,
 * dan jari kanan tetap di satu tempat sepanjang shift.
 *
 * ### ⭐ Logo BRAND jadi hero, logo OSB POS kecil di kaki
 * Staf bekerja untuk brand-nya, bukan untuk kita. Menaruh logo vendor di atas logo klien pada
 * layar yang ditatap sepanjang shift adalah vendor yang menempatkan diri di tengah.
 *
 * ⚠️ **Pesan galat sengaja SERAGAM** untuk "kode staf tak ada" dan "PIN salah". Membedakannya
 * memberi tahu penyerang kode staf mana yang nyata — dan itu setengah jalan menuju menebak PIN-nya.
 * Server sudah menyeragamkannya; layar tak boleh membocorkannya kembali dengan pesan yang lebih
 * "membantu".
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { checkOfflinePin, mintOfflineVerifier } from '@osbpos/domain';
import type { OfflineVerifier } from '@osbpos/domain';

import { api, ApiFailure } from '../api/client';
import type { Branding } from '../api/client';
import { CACHE_KEYS, cacheGet, cacheSet } from '../api/cache';
import { saveStaffSession } from '../api/session';

export interface PinLoginProps {
  readonly outletCode: string;
  /** ⛔ Wajib: verifier luring diikat ke PERANGKAT, bukan cuma ke stafnya (lihat `offline-pin`). */
  readonly deviceCode: string;
  readonly onLogin: () => void;
}

/**
 * Verifier + secukupnya identitas untuk membentuk sesi tanpa server.
 *
 * ⛔ Izin ikut disalin karena luring tak ada yang bisa ditanyai — dan itu **satu-satunya** hal di
 * sini yang tak bisa dibuat aman: staf yang izinnya dicabut di portal tetap memegang izin lamanya
 * sampai salinannya kedaluwarsa (`OFFLINE_LOGIN_MAX_AGE_MS`, 7 hari).
 */
type StoredVerifier = OfflineVerifier & {
  readonly displayName: string;
  readonly permissions: readonly string[];
};

/** Titik yang ditampilkan. ⚠️ Panjang benar/salahnya PIN tetap urusan SERVER. */
const PIN_DOTS = 6;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export function PinLogin({ outletCode, deviceCode, onLogin }: PinLoginProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState<Branding | null>(null);

  /*
   * Identitas outlet datang dari SERVER, bukan dipahat di bundel: satu bundel dipakai semua
   * outlet, dan nama yang dipahat akan salah di outlet berikutnya.
   *
   * ⚠️ Gagal muat TIDAK menghalangi login — kasir tetap harus bisa bekerja meski logonya tak
   * datang. Yang hilang cuma hiasan; yang tak boleh hilang adalah pintunya.
   */
  useEffect(() => {
    let alive = true;
    void api
      .branding(outletCode)
      .then((b) => { if (alive) setBrand(b); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [outletCode]);

  const allowed = code.trim() !== '' && pin.length >= 4 && !busy;

  /**
   * ⛔⛔ **Jalur LURING — hanya saat server benar-benar tak terjangkau.**
   *
   * Dipanggil dari `catch` yang bukan `ApiFailure`: server yang MENJAWAB "PIN salah" adalah
   * jawaban yang sah dan final. Menjatuhkannya ke salinan lokal berarti PIN yang baru saja dicabut
   * di portal tetap membuka layar — dan pemiliknya tak akan pernah tahu.
   */
  const masukLuring = useCallback(async (staffCode: string): Promise<void> => {
    const simpan = cacheGet<StoredVerifier>(CACHE_KEYS.offlinePin(staffCode))?.value;
    if (simpan === undefined) {
      /*
       * ⚠️ Pesan BERBEDA, dan sengaja: "PIN salah" akan membuat kasir mengetik ulang PIN yang
       * sebenarnya benar sampai terkunci. Yang kurang di sini bukan PIN-nya melainkan satu kali
       * login DARING di perangkat ini.
       */
      setError(t('session.loggedIn.offlineUnknown'));
      return;
    }

    const r = await checkOfflinePin(simpan, { staffCode, deviceCode, pin });
    // ⭐ Keadaan baru SELALU disimpan — di situlah hitungan gagal & kuncinya hidup.
    cacheSet(CACHE_KEYS.offlinePin(staffCode), r.next);

    if (r.outcome === 'locked') { setError(t('session.loggedIn.locked')); return; }
    if (r.outcome === 'expired') { setError(t('session.loggedIn.offlineExpired')); return; }
    if (r.outcome === 'wrong') { setError(t('session.loggedIn.failed')); return; }

    /*
     * ⛔ Tanpa token staf — tak ada yang bisa menandatanganinya selain server. Permintaan yang
     * menuntut token akan ditolak, dan itu BENAR: yang boleh dikerjakan luring cuma yang memang
     * tak menyentuh server (menyusun penjualan, lalu mengantre).
     */
    saveStaffSession({
      staffCode,
      displayName: simpan.displayName,
      permissions: simpan.permissions,
      token: '',
      /* ⭐ Ditandai sebagai sesi salinan — shell yang menampilkannya (lihat `CashierShell`). */
      offline: true,
    });
    onLogin();
  }, [deviceCode, onLogin, pin, t]);

  const submit = useCallback(() => {
    if (code.trim() === '' || pin.length < 4 || busy) return;
    const staffCode = code.trim();
    setBusy(true);
    setError(null);
    void api
      .loginPin({ staffCode, pin })
      .then(async (r) => {
        saveStaffSession({
          staffCode: r.staffCode,
          displayName: r.staffName,
          permissions: r.permissions,
          token: r.staff_token,
        });
        /*
         * ⭐⭐⭐ Verifier luring dicetak DI SINI — sesudah server membuktikan PIN-nya benar, dari
         * PIN yang barusan diketik. ⛔ Tak ada apa pun yang turun dari server: `pin_hash` tetap
         * tinggal di sana (aturan yang sama dengan outlet-server), dan yang tersimpan di tablet
         * cuma bayangan berumur 7 hari dari sesuatu yang memang pernah diketik di mesin ini.
         *
         * ⚠️ Kegagalannya tak menghalangi login — kasir sudah masuk. Yang hilang cuma kemampuan
         * masuk lagi saat luring, dan itu bukan alasan menahan orang di depan antrean.
         */
        try {
          const v = await mintOfflineVerifier({ staffCode: r.staffCode, deviceCode, pin });
          cacheSet(CACHE_KEYS.offlinePin(r.staffCode), {
            ...v, displayName: r.staffName, permissions: r.permissions,
          });
        } catch { /* diabaikan dengan sengaja */ }
        // ⛔ PIN dibuang dari memori layar begitu dipakai — PIN yang tertinggal di state adalah
        // PIN yang bisa terbaca dari devtools oleh siapa pun yang lewat.
        setPin('');
        onLogin();
      })
      .catch(async (e: unknown) => {
        /*
         * ⭐ Dua kegagalan yang BERBEDA. `ApiFailure` = server menjawab dan menolak ⇒ final.
         * Selain itu (fetch menolak) = server tak terjangkau ⇒ barulah salinan lokal berlaku.
         */
        if (e instanceof ApiFailure) {
          /*
           * ⭐⭐ **Terkunci ≠ salah** — dan menyamakannya justru memperburuk keadaannya.
           *
           * ⛔ Aturan "pesan SERAGAM" berlaku untuk membedakan *"kode staf tak ada"* dari *"PIN
           * salah"* — itu yang membocorkan kode mana yang nyata. Ia TIDAK berlaku untuk
           * penguncian: yang terkunci sudah pasti kode yang nyata (server tak mengunci kode yang
           * tak ada), jadi menyebutkannya tak membocorkan apa pun.
           *
           * ⚠️ Yang hilang kalau disamakan: kasir melihat "PIN salah" pada akun yang sebenarnya
           * terkunci, lalu mencoba lagi dan lagi — memperpanjang kuncinya sendiri — untuk masalah
           * yang cuma butuh MENUNGGU. (Ketahuan saat menelusuri laporan Imron, 2026-08-06.)
           */
          setError(t(
            e.detail.error_code === 'AUTH-423-PIN_LOCKED'
              ? 'session.loggedIn.locked'
              : 'session.loggedIn.failed',
          ));
          setPin('');
          return;
        }
        try { await masukLuring(code.trim()); } finally { setPin(''); }
      })
      .finally(() => { setBusy(false); });
  }, [busy, code, deviceCode, masukLuring, onLogin, pin, t]);

  const press = (d: string) => {
    setError(null);
    setPin((p) => (p.length >= PIN_DOTS ? p : p + d));
  };

  return (
    <section className="pin-login" aria-label={t('session.loggedIn.title')} data-testid="pin-login">
      <header className="pin-brand">
        {/*
          ⭐ Logo bila ada; kalau tidak, NAMA BRAND dalam huruf besar — bukan kotak kosong.
          Aturan yang sama dipakai kartu menu: yang tanpa gambar tetap DIRANCANG, bukan sisa ruang.
        */}
        {brand?.logoUrl == null ? (
          <div className="pin-brand-name" data-testid="brand-name">{brand?.brandName ?? ''}</div>
        ) : (
          <img
            className="pin-brand-logo"
            data-testid="brand-logo"
            src={brand.logoUrl}
            alt={brand.brandName}
            /* Logo yang gagal dimuat jatuh ke nama brand — tak pernah jadi ikon rusak. */
            onError={() => { setBrand({ ...brand, logoUrl: null }); }}
          />
        )}
        <p className="pin-outlet" data-testid="brand-outlet">
          {brand === null
            ? outletCode
            : brand.logoUrl === null
              ? brand.outletName
              : `${brand.brandName} · ${brand.outletName}`}
        </p>
      </header>

      <div className="pin-body">
        <div className="pin-fields">
          <label>
            {t('session.loggedIn.staffCode')}
            {/*
              ⭐⭐ **Diubah jadi HURUF BESAR sambil diketik** (Imron, 2026-08-06).

              Kode staf hampir selalu terdaftar huruf besar (`IMRON`, `EMP-001`, `KSR-01`), dan
              layar yang menampilkan `imron` membuat kasir mengira kodenya memang begitu.

              ⚠️ Ini SEMATA kejelasan, bukan kontrol: server sudah membandingkan tanpa peduli huruf
              besar/kecil. ⛔ Menyandarkan kebenaran pada transformasi di layar berarti kasir yang
              menempel kode dari catatan — atau memakai keyboard yang tak memicu `onChange` seperti
              yang diharapkan — kembali ditolak, dengan pesan yang tak menyebut sebabnya.

              ⚠️ `autoCapitalize` DIPERTAHANKAN: ia yang membuat keyboard ponsel terbuka dalam mode
              huruf besar, jadi jarinya pun tak perlu berpindah. Keduanya menjawab hal berbeda.
            */}
            <input
              data-testid="pin-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              autoComplete="off"
              autoCapitalize="characters"
            />
          </label>

          {/* ⭐ PIN sebagai TITIK, bukan kolom teks — keypad-lah yang mengisinya. */}
          <div className="pin-dots-wrap">
            <span className="pin-label">PIN</span>
            <div className="pin-dots" data-testid="pin-dots" aria-label={`PIN ${String(pin.length)}`}>
              {Array.from({ length: PIN_DOTS }, (_, i) => (
                <i key={i} className={i < pin.length ? 'pin-dot on' : 'pin-dot'} />
              ))}
            </div>
          </div>

          {error !== null && <p className="error" data-testid="pin-error">{error}</p>}
        </div>

        <div className="pin-keypad" role="group" aria-label="Keypad PIN">
          {KEYS.map((d) => (
            <button key={d} type="button" data-testid={`key-${d}`} onClick={() => { press(d); }}>
              {d}
            </button>
          ))}
          <button
            type="button"
            className="pin-key-sub"
            data-testid="key-backspace"
            aria-label={t('common.back')}
            onClick={() => { setPin((p) => p.slice(0, -1)); setError(null); }}
          >
            ⌫
          </button>
          <button type="button" data-testid="key-0" onClick={() => { press('0'); }}>0</button>
          {/*
            ⛔ Nonaktif sampai benar-benar bisa dikirim. Tombol yang pasti gagal memancing kasir
            mengetuk berulang — dan pengulangan memakan jatah percobaan sebelum akun terkunci.
          */}
          <button
            type="button"
            className="pin-key-ok"
            data-testid="pin-submit"
            disabled={!allowed}
            onClick={submit}
          >
            {busy ? '…' : '→'}
          </button>
        </div>
      </div>

      {/* ⚠️ Provenans, bukan iklan: kecil, redup, di kaki. */}
      <footer className="pin-foot"><span className="pin-osbpos">osbpos</span></footer>
    </section>
  );
}
